import { useState, useEffect, useMemo } from 'react';
import { Header } from '../components/Layout';
import { useParks } from '../contexts/ParksContext';
import {
  ComposedChart, LineChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { X, Lock, ChevronDown, Info } from 'lucide-react';

/**
 * Juhtimine — Markedroid-stiilis 24h plaanikava per-park.
 *
 * Vt design-spec: markedroid-chart-spec.md
 *
 * Hetkel toetab:
 *  - Pargi valimine
 *  - Periood Täna / Homme
 *  - Timeline bar 24h × 15min plaani visualisatsioon
 *  - Chart börsihinnaga + plaanitud režiimi värvid
 *  - Muuda plaani modal: 15min slot-id, mode dropdown
 *
 * Veel TODO: backend salvestus + reaalsed käsklused.
 * Käske EI saadeta hetkel — see on UI prototüüp.
 */

// Režiimid (Markedroid spec §7.3) + kirjeldused
const MODES = {
  battery_use:    {
    label: 'Aku kasutus', color: '#97D119',
    desc: 'Avatud aku — tarbimine kaetakse päikese ja aku abil. Üleliigne päikeseenergia salvestatakse akusse. Akut kasutatakse või laetakse vastavalt vajadusele.',
  },
  charging:       {
    label: 'Laadimine', color: '#1493C9',
    desc: 'Laeb akut elektrivõrgust. Laadimisvõimsust saab piirata "Impordi limiit" funktsiooniga.',
  },
  export_prio:    {
    label: 'Prioriteet ekspordil', color: '#82033E',
    desc: 'Tarbimine kaetakse päikeseenergiaga ja üleliigne energia müüakse võrku. Kõige kallimate hommikuste/päevaste tundide jaoks. Aku kasutamine lubatud.',
  },
  max_sell:       {
    label: 'Maksimaalne müük', color: '#FF5900',
    desc: 'Inverter müüb aku energiat võrku maksimaalse lubatud võimsusega.',
  },
  hold:           {
    label: 'Hoia ja kogu', color: '#D1D1D1',
    desc: 'Toodetud päikeseenergia katab tarbimise ja ülejäägiga laetakse akut. Aku ei kasutata, tarbimine kaetakse võrgust.',
  },
  stop:           {
    label: 'Peata juhtimine', color: '#000000',
    desc: 'Peatab kogu EMS-seadme juhtimise. EMS loeb ainult andmeid inverterist.',
  },
  pause:          {
    label: 'Aku pausil', color: '#303030',
    desc: 'Peatab aku kasutamise.',
  },
};

const MODE_KEYS = Object.keys(MODES);
// Vaikimisi režiim — 'stop' tähendab et EMS ei sekku, kõik on inverteri enda otsustada.
// Kasutaja peab teadlikult valima mõne aktiivse režiimi (battery_use, charging jne).
const DEFAULT_MODE = 'stop';
const SLOTS_PER_DAY = 96; // 24 × 4 (15min)

function defaultPlan() {
  return Array.from({ length: SLOTS_PER_DAY }, () => DEFAULT_MODE);
}

async function loadPlan(slot, dateStr) {
  try {
    const r = await fetch(`/api/sites/${slot}/plan?date=${dateStr}`);
    if (!r.ok) return defaultPlan();
    const d = await r.json();
    if (Array.isArray(d.modes) && d.modes.length === SLOTS_PER_DAY) return d.modes;
  } catch (_) { /* ignored */ }
  return defaultPlan();
}

async function savePlan(slot, dateStr, plan, editMode = 'locked_all') {
  try {
    const r = await fetch(`/api/sites/${slot}/plan?date=${dateStr}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ modes: plan, edit_mode: editMode }),
    });
    return r.ok;
  } catch (_) { return false; }
}

function formatTime(slotIdx) {
  const h = Math.floor(slotIdx / 4);
  const m = (slotIdx % 4) * 15;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function dateStrFor(periodOffset) {
  // Local date (mitte UTC) — peab klappima backend Europe/Tallinn ajaga.
  const d = new Date();
  d.setDate(d.getDate() + periodOffset);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function ControlPage({ wsData, wsConnected }) {
  const { data } = useParks();
  const onlineParks = useMemo(() => (data?.parks || []).filter(p => p.online && !p.disabled), [data]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [period, setPeriod] = useState('today'); // 'today' | 'tomorrow'
  const [plan, setPlan] = useState(defaultPlan());
  const [modalOpen, setModalOpen] = useState(false);
  const [prices, setPrices] = useState([]);  // Nord Pool börsihind
  const [socHistory, setSocHistory] = useState([]);  // SoC ajalugu valitud kuupäeva jaoks
  const [solarForecast, setSolarForecast] = useState([]); // Päikese tootmise ennustus (forecast.solar)
  const [solarActual, setSolarActual] = useState([]);     // Tegelik tootmine täna (InfluxDB pv_meter)
  const [gridActual, setGridActual] = useState([]);       // Grid kWh täna (+import, -export)
  const [pcsActual, setPcsActual] = useState([]);         // PCS kWh täna (+charge, -discharge)
  const [economics, setEconomics] = useState(null);       // Tulu/kulu täna (EconomicsTracker)
  const [autoSocTraj, setAutoSocTraj] = useState([]);     // AutoPlanner SoC ennustus per slot
  const [profitForecast, setProfitForecast] = useState(null);  // EMHASS ennustatav tulu täna + homme
  // Legend toggle — millised sarjad on peidetud (salvestub localStorage-i)
  const [hidden, setHidden] = useState(() => {
    try { return JSON.parse(localStorage.getItem('controlPageHidden') || '{}'); }
    catch { return {}; }
  });
  const toggleHidden = (key) => {
    setHidden(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem('controlPageHidden', JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const [now, setNow] = useState(Date.now());

  const dateStr = useMemo(() => dateStrFor(period === 'today' ? 0 : 1), [period]);

  // Aktualne kellaaeg — uuendab iga 30s + kui tab tuleb tagasi fookusesse
  // (taustal töötavad tabid throttle-tatakse Chrome-is 60s+ peale, mis põhjustab stale line-i).
  useEffect(() => {
    const tick = () => setNow(Date.now());
    const iv = setInterval(tick, 30_000);
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', tick);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', tick);
    };
  }, []);

  // Plaan backend-ist kui park või kuupäev muutub
  useEffect(() => {
    if (selectedSlot == null) return;
    let cancelled = false;
    (async () => {
      const p = await loadPlan(selectedSlot, dateStr);
      if (!cancelled) setPlan(p);
    })();
    return () => { cancelled = true; };
  }, [selectedSlot, dateStr]);

  // Vali esimene park kui ükski pole valitud
  useEffect(() => {
    if (selectedSlot == null && onlineParks.length > 0) {
      setSelectedSlot(onlineParks[0].slot);
    }
  }, [onlineParks, selectedSlot]);

  // Lae börsihind valitud kuupäevale (sama API kui BorsihindPage, aga slot-spetsiifiliselt
  // et eraisiku parkidel oleks müügihind KM-ita)
  useEffect(() => {
    const from = new Date(dateStr + 'T00:00:00').getTime();
    const to = from + 86400_000 - 1;
    const slotQ = selectedSlot != null ? `&slot=${selectedSlot}` : '';
    fetch(`/api/nordpool/prices?from=${from}&to=${to}${slotQ}`)
      .then(r => r.json())
      .then(d => setPrices(Array.isArray(d.points) ? d.points : []))
      .catch(() => setPrices([]));
  }, [dateStr, selectedSlot]);

  // Lae aku SoC ajalugu — uuendub ka iga minuti tagant et joon kasvaks reaalajas
  useEffect(() => {
    if (selectedSlot == null) return;
    let cancelled = false;
    const fetchSoc = () => {
      fetch(`/api/sites/${selectedSlot}/soc-history?date=${dateStr}`)
        .then(r => r.json())
        .then(d => { if (!cancelled) setSocHistory(Array.isArray(d.points) ? d.points : []); })
        .catch(() => { if (!cancelled) setSocHistory([]); });
    };
    fetchSoc();
    const iv = setInterval(fetchSoc, 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [selectedSlot, dateStr]);

  // Lae päikese tootmise ennustus (forecast.solar) — uuendub iga 10 min (backend pollib 60min)
  useEffect(() => {
    if (selectedSlot == null) return;
    let cancelled = false;
    const fetchForecast = () => {
      fetch(`/api/sites/${selectedSlot}/solar-forecast`)
        .then(r => r.json())
        .then(d => { if (!cancelled) setSolarForecast(Array.isArray(d.points) ? d.points : []); })
        .catch(() => { if (!cancelled) setSolarForecast([]); });
    };
    fetchForecast();
    const iv = setInterval(fetchForecast, 10 * 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [selectedSlot]);

  // Lae tegelik päikese, grid ja PCS — uuendub iga minut
  useEffect(() => {
    if (selectedSlot == null) return;
    let cancelled = false;
    const fetchAll = async () => {
      try {
        const [sR, gR, pR] = await Promise.all([
          fetch(`/api/sites/${selectedSlot}/solar-actual?date=${dateStr}`).then(r => r.json()),
          fetch(`/api/sites/${selectedSlot}/grid-actual?date=${dateStr}`).then(r => r.json()),
          fetch(`/api/sites/${selectedSlot}/pcs-actual?date=${dateStr}`).then(r => r.json()),
        ]);
        if (cancelled) return;
        setSolarActual(Array.isArray(sR.points) ? sR.points : []);
        setGridActual(Array.isArray(gR.points) ? gR.points : []);
        setPcsActual(Array.isArray(pR.points) ? pR.points : []);
      } catch {
        if (!cancelled) { setSolarActual([]); setGridActual([]); setPcsActual([]); }
      }
    };
    fetchAll();
    const iv = setInterval(fetchAll, 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [selectedSlot, dateStr]);

  // Lae tulu/kulu (EconomicsTracker) — täna kogu päev
  useEffect(() => {
    if (selectedSlot == null) return;
    let cancelled = false;
    const fetchEcon = () => {
      const dayStart = new Date(dateStr + 'T00:00:00').getTime();
      const dayEnd = dayStart + 86400_000 - 1;
      fetch(`/api/sites/${selectedSlot}/economics?from=${dayStart}&to=${dayEnd}`)
        .then(r => r.json())
        .then(d => { if (!cancelled) setEconomics(d.totals || null); })
        .catch(() => { if (!cancelled) setEconomics(null); });
    };
    fetchEcon();
    const iv = setInterval(fetchEcon, 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [selectedSlot, dateStr]);

  // Lae AutoPlanner SoC ennustus
  useEffect(() => {
    if (selectedSlot == null) return;
    let cancelled = false;
    const fetchSoc = () => {
      fetch(`/api/sites/${selectedSlot}/auto-plan-soc?date=${dateStr}`)
        .then(r => r.json())
        .then(d => { if (!cancelled) setAutoSocTraj(Array.isArray(d.points) ? d.points : []); })
        .catch(() => { if (!cancelled) setAutoSocTraj([]); });
    };
    fetchSoc();
    const iv = setInterval(fetchSoc, 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [selectedSlot, dateStr]);

  // Lae ennustatav tulu — ALATI tänase päeva ennustus (sõltumata valitud Täna/Homme tab-ist).
  // Põhjus: tulu-prognoos on alati "tänase + homse" tulevikku-vaatav, mitte tab-i sõltuv.
  // Iga 5 min (EMHASS on raske arvutus).
  useEffect(() => {
    if (selectedSlot == null) return;
    let cancelled = false;
    const todayLocal = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Tallinn', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
    const fetchForecast = () => {
      fetch(`/api/sites/${selectedSlot}/plan-compare?date=${todayLocal}&levels=EMHASS`)
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (!cancelled && d) {
            const e = d.EMHASS || {};
            if (!e.error) {
              setProfitForecast({
                todayTotal: e.todayProfitEur ?? 0,
                todayArbitrage: e.todayArbitrageEur ?? 0,
                todayRenewable: e.todayRenewableSupportEur ?? 0,
                tomorrowTotal: e.tomorrowProfitEur ?? 0,
                tomorrowArbitrage: e.tomorrowArbitrageEur ?? 0,
                tomorrowRenewable: e.tomorrowRenewableSupportEur ?? 0,
                horizonHours: e.horizonHours ?? 24,
              });
            }
          }
        })
        .catch(() => { /* ignore — keepa vana profitForecast */ });
    };
    fetchForecast();
    const iv = setInterval(fetchForecast, 5 * 60_000);  // 5 min
    return () => { cancelled = true; clearInterval(iv); };
  }, [selectedSlot]);  // EI sõltu dateStr-ist — kasti väärtused jäävad samad Täna/Homme tab vahel

  // "Praegu" silt — uuendub iga 60s automaatselt (sõltub `now`-ist).
  // XAxis on kategooriline (string-väärtused "HH:MM"), seega leiame lähima slot-i time-stringi.
  const nowLabel = useMemo(() => {
    if (period !== 'today') return null;
    const d = new Date(now);
    const slotIdx = d.getHours() * 4 + Math.floor(d.getMinutes() / 15);
    return formatTime(slotIdx);
  }, [period, now]);

  // Praegune slot-indeks (kui vaadame täna)
  const currentSlot = useMemo(() => {
    if (period !== 'today') return null;
    const d = new Date();
    return d.getHours() * 4 + Math.floor(d.getMinutes() / 15);
  }, [period, now]);

  const selectedPark = onlineParks.find(p => p.slot === selectedSlot);

  // Chart andmed: 96 punkti, igaüks turuhind + osuthind + müügihind + mode + SoC
  const chartData = useMemo(() => {
    const dayStart = new Date(dateStr + 'T00:00:00').getTime();
    const market = new Array(SLOTS_PER_DAY).fill(null);
    const buy    = new Array(SLOTS_PER_DAY).fill(null);
    const sell   = new Array(SLOTS_PER_DAY).fill(null);
    const soc    = new Array(SLOTS_PER_DAY).fill(null);
    const fcst   = new Array(SLOTS_PER_DAY).fill(0);
    const act    = new Array(SLOTS_PER_DAY).fill(null);
    const gridImp = new Array(SLOTS_PER_DAY).fill(null);  // grid+ → import
    const gridExp = new Array(SLOTS_PER_DAY).fill(null);  // grid− → eksport (positiivseks teisendatud)
    const akuChg = new Array(SLOTS_PER_DAY).fill(null);   // PCS+ → laadimine
    const akuDsg = new Array(SLOTS_PER_DAY).fill(null);   // PCS− → kasutus (positiivseks teisendatud)
    const cons   = new Array(SLOTS_PER_DAY).fill(null);   // tarbimine = solar + grid - PCS
    const socPred = new Array(SLOTS_PER_DAY).fill(null);  // AutoPlanner SoC ennustus
    autoSocTraj.forEach(p => {
      if (p.slot_idx >= 0 && p.slot_idx < SLOTS_PER_DAY && p.soc != null) {
        socPred[p.slot_idx] = p.soc;
      }
    });
    // Elering tagastab nüüd 15-min hindu — sama timestamp kohta ainult 1 slot
    // (vanasti tunnipõhine, kus jaotaisime 4 slot-i; nüüd otsene 1:1 mapping).
    // Detect samm: kui kahe järjestikuse hinnapunkti vahe on >= 45 min, jaotame 4-ks.
    const stepMs = prices.length > 1 ? (prices[1].ts - prices[0].ts) : 3600_000;
    const isHourly = stepMs >= 30 * 60_000;
    prices.forEach(p => {
      const slotIdx = Math.floor((p.ts - dayStart) / (15 * 60_000));
      if (slotIdx >= 0 && slotIdx < SLOTS_PER_DAY) {
        const count = isHourly ? 4 : 1;
        for (let k = 0; k < count; k++) {
          const idx = slotIdx + k;
          if (idx < SLOTS_PER_DAY) {
            market[idx] = (p.price_kwh      || 0) * 100;
            buy[idx]    = (p.price_buy_kwh  || 0) * 100;
            sell[idx]   = (p.price_sell_kwh || 0) * 100;
          }
        }
      }
    });
    socHistory.forEach(p => {
      if (p.soc == null) return;
      const slotIdx = Math.floor((p.ts - dayStart) / (15 * 60_000));
      if (slotIdx >= 0 && slotIdx < SLOTS_PER_DAY) {
        soc[slotIdx] = p.soc;
      }
    });
    // Forecast.solar tasuta tier tagastab TUNNI-põhised punktid (kWh selle tunni kohta).
    // Jagame iga tunni 4 × 15-min slotiks (ühtlane jaotus tunni piires).
    const sortedFcst = [...solarForecast].sort((a, b) => a.ts - b.ts);
    sortedFcst.forEach((p, idx) => {
      if (p.kwh == null) return;
      const startMs = p.ts;
      const nextMs = idx + 1 < sortedFcst.length ? sortedFcst[idx + 1].ts : startMs + 3600_000;
      const durationMs = Math.max(15 * 60_000, nextMs - startMs);
      const startSlot = Math.floor((startMs - dayStart) / (15 * 60_000));
      const numSlots = Math.max(1, Math.round(durationMs / (15 * 60_000)));
      const perSlot = p.kwh / numSlots;
      for (let k = 0; k < numSlots; k++) {
        const idxK = startSlot + k;
        if (idxK >= 0 && idxK < SLOTS_PER_DAY) fcst[idxK] += perSlot;
      }
    });
    // Tegelik tootmine 15-min sammuga
    solarActual.forEach(p => {
      if (p.kwh == null) return;
      const slotIdx = Math.floor((p.ts - dayStart) / (15 * 60_000));
      if (slotIdx >= 0 && slotIdx < SLOTS_PER_DAY) act[slotIdx] = Math.max(0, p.kwh);
    });
    // Grid: positiivne = import, negatiivne = export. Lahutame kahte ribasse.
    gridActual.forEach(p => {
      if (p.kwh == null) return;
      const slotIdx = Math.floor((p.ts - dayStart) / (15 * 60_000));
      if (slotIdx < 0 || slotIdx >= SLOTS_PER_DAY) return;
      if (p.kwh > 0) gridImp[slotIdx] = p.kwh;
      else if (p.kwh < 0) gridExp[slotIdx] = -p.kwh;  // ekspordi näitame positiivse väärtusena
    });
    // PCS: positiivne = laadimine, negatiivne = kasutus (tühjenemine)
    pcsActual.forEach(p => {
      if (p.kwh == null) return;
      const slotIdx = Math.floor((p.ts - dayStart) / (15 * 60_000));
      if (slotIdx < 0 || slotIdx >= SLOTS_PER_DAY) return;
      if (p.kwh > 0) akuChg[slotIdx] = p.kwh;
      else if (p.kwh < 0) akuDsg[slotIdx] = -p.kwh;
    });
    // Tarbimine = solar + grid - PCS. Cap 0-ga (mõõtmise ajalise nihe võib teha negatiivseks).
    for (let i = 0; i < SLOTS_PER_DAY; i++) {
      const s = act[i] != null ? act[i] : 0;
      const g = (gridImp[i] != null ? gridImp[i] : 0) - (gridExp[i] != null ? gridExp[i] : 0);
      const p = (akuChg[i] != null ? akuChg[i] : 0) - (akuDsg[i] != null ? akuDsg[i] : 0);
      // Kui ühtegi sarja andmeid pole → null (mitte 0)
      const hasAny = act[i] != null || gridImp[i] != null || gridExp[i] != null || akuChg[i] != null || akuDsg[i] != null;
      cons[i] = hasAny ? Math.max(0, s + g - p) : null;
    }
    return Array.from({ length: SLOTS_PER_DAY }, (_, i) => ({
      slot: i,
      time: formatTime(i),
      market: market[i],
      buy:    buy[i],
      sell:   sell[i],
      soc:    soc[i],
      forecast_kwh: fcst[i] > 0.01 ? fcst[i] : null,
      actual_kwh:   act[i] != null && act[i] > 0.01 ? act[i] : null,
      grid_import:  gridImp[i] != null && gridImp[i] > 0.01 ? gridImp[i] : null,
      grid_export:  gridExp[i] != null && gridExp[i] > 0.01 ? gridExp[i] : null,
      aku_laadimine: akuChg[i] != null && akuChg[i] > 0.01 ? akuChg[i] : null,
      aku_kasutus:   akuDsg[i] != null && akuDsg[i] > 0.01 ? akuDsg[i] : null,
      tarbimine:     cons[i] != null && cons[i] > 0.01 ? cons[i] : null,
      soc_predicted: socPred[i],
      mode:   plan[i],
    }));
  }, [prices, plan, socHistory, solarForecast, solarActual, gridActual, pcsActual, autoSocTraj, dateStr]);

  const currentMode = currentSlot != null ? plan[currentSlot] : null;

  return (
    <div className="h-full flex flex-col" style={{ background: 'var(--bg)' }}>
      <Header title="Juhtimine" connected={wsConnected} />

      <div style={{ flex: 1, overflow: 'auto', padding: '20px 28px 40px', maxWidth: 1480, width: '100%', margin: '0 auto' }}>
        {/* Pargi valija */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: '0.7rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 500, marginRight: 12 }}>Park</label>
          <select
            value={selectedSlot ?? ''}
            onChange={(e) => setSelectedSlot(Number(e.target.value))}
            style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--border-strong)', fontSize: '0.88rem', background: 'white' }}
          >
            {onlineParks.map(p => (
              <option key={p.slot} value={p.slot}>{p.slot} — {p.name}</option>
            ))}
          </select>
          <span style={{ marginLeft: 16, fontSize: '0.72rem', color: 'var(--text-3)' }}>
            SoC piirid määratakse Hooldus → Seadistused lehel
          </span>
        </div>

        {/* PV CURTAILMENT BANNER — Negatiivse spot-hinna kaitse */}
        {selectedPark?.control?.pv_curtail_enabled && (() => {
          const active = selectedPark.control.pv_curtail_active === true;
          const dryRun = selectedPark.control.pv_curtail_dry_run !== false;
          const spotC = selectedPark.control.pv_curtail_spot_kwh != null
            ? (selectedPark.control.pv_curtail_spot_kwh * 100).toFixed(2)
            : '?';
          const soc = selectedPark.control.pv_curtail_soc != null
            ? selectedPark.control.pv_curtail_soc.toFixed(1)
            : '?';
          const bg = active ? '#FEF3C7' : '#F3F4F6';
          const border = active ? '#F59E0B' : '#D1D5DB';
          const txt = active ? '#92400E' : '#374151';
          return (
            <div style={{
              background: bg, border: `1px solid ${border}`, borderRadius: 8,
              padding: '10px 14px', marginBottom: 16, display: 'flex',
              justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 20 }}>{active ? '🌥️' : '☀️'}</span>
                <div style={{ fontSize: 13, color: txt }}>
                  <div style={{ fontWeight: 600 }}>
                    Negatiivse spot-hinna kaitse: {active ? 'AKTIIVNE' : 'ootel'}
                    {dryRun && <span style={{ marginLeft: 8, padding: '2px 6px', background: '#E5E7EB', borderRadius: 3, fontSize: 11 }}>DRY-RUN</span>}
                  </div>
                  <div style={{ fontSize: 11, color: txt, opacity: 0.85, marginTop: 2 }}>
                    Spot: {spotC} c/kWh · SoC: {soc}% · Lävi: spot &lt; −0.1 c/kWh JA SoC ≥ 85%
                    {active && ' · Inverter Export Limit = 0%'}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Markedroid-stiilis kaart */}
        <div style={{
          background: '#FFFFFF', border: '1px solid #DDDFE1', borderRadius: 14,
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px -1px rgba(0,0,0,0.1)',
        }}>
          <div style={{ padding: 16 }}>
            {/* Header rida */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 18, fontWeight: 600, color: '#11093E', lineHeight: '24px' }}>
                  Prognoos (24h)
                </span>
                <Info size={16} style={{ color: '#11093E', cursor: 'pointer' }} />
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {/* Täna/Homme toggle */}
                <ToggleGroup>
                  <ToggleBtn active={period === 'today'} onClick={() => setPeriod('today')}>Täna</ToggleBtn>
                  <ToggleBtn active={period === 'tomorrow'} onClick={() => setPeriod('tomorrow')}>Homme</ToggleBtn>
                </ToggleGroup>
                {/* 15m konstantne (placeholder) */}
                <ToggleGroup>
                  <ToggleBtn active>15m</ToggleBtn>
                </ToggleGroup>
                <button
                  onClick={() => setModalOpen(true)}
                  style={{
                    background: '#FFFFFF', border: '1px solid #DDDFE1', borderRadius: 4,
                    color: '#000', fontSize: 14, fontWeight: 600, padding: '5px 16px', height: 33, cursor: 'pointer',
                  }}
                >
                  Muuda plaani
                </button>
              </div>
            </div>

            {/* HETKEL rida */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 14 }}>
              <span style={{ fontWeight: 700 }}>HETKEL:</span>
              <span>Inverteri olek:</span>
              {currentMode ? (
                <span style={{ fontWeight: 700, color: '#11093E', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{
                    display: 'inline-block', width: 12, height: 12, borderRadius: '50%',
                    background: MODES[currentMode]?.color || '#ccc',
                  }} />
                  {MODES[currentMode]?.label || currentMode} {nowLabel && `(${nowLabel})`}
                </span>
              ) : (
                <span style={{ color: '#8C8C8C' }}>—</span>
              )}
            </div>

            {/* Tulu/Kulu täna — EconomicsTracker andmed */}
            {economics && (
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8,
                marginBottom: 12, fontSize: 12, background: '#F9FAF9',
                border: '1px solid var(--border)', borderRadius: 6, padding: '10px 12px',
              }}>
                <div>
                  <div style={{ color: '#8C8C8C', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tulu täna</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#15803d' }}>
                    +{(economics.revenue_export || 0).toFixed(2)} €
                  </div>
                  <div style={{ color: '#8C8C8C', fontSize: 10 }}>eksport {(economics.grid_export_kwh || 0).toFixed(0)} kWh</div>
                </div>
                <div>
                  <div style={{ color: '#8C8C8C', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Kulu täna</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#dc2626' }}>
                    −{(economics.cost_import || 0).toFixed(2)} €
                  </div>
                  <div style={{ color: '#8C8C8C', fontSize: 10 }}>import {(economics.grid_import_kwh || 0).toFixed(0)} kWh</div>
                </div>
                <div>
                  <div style={{ color: '#8C8C8C', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Neto</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: (economics.net_eur || 0) >= 0 ? '#15803d' : '#dc2626' }}>
                    {(economics.net_eur || 0) >= 0 ? '+' : ''}{(economics.net_eur || 0).toFixed(2)} €
                  </div>
                  <div style={{ color: '#8C8C8C', fontSize: 10 }}>tulu − kulu</div>
                </div>
                <div>
                  <div style={{ color: '#8C8C8C', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Aku säästis</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#11093E' }}>
                    {(economics.battery_savings || 0) >= 0 ? '+' : ''}{(economics.battery_savings || 0).toFixed(2)} €
                  </div>
                  <div style={{ color: '#8C8C8C', fontSize: 10 }}>vs ilma akuta</div>
                </div>
              </div>
            )}

            {/* Ennustatav tulu — EMHASS MILP arvutus (täna + homme), uuendub iga 5 min */}
            {profitForecast && (
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8,
                marginBottom: 12, fontSize: 12, background: '#FFF8E1',
                border: '1px solid #FFCA28', borderRadius: 6, padding: '10px 12px',
              }}>
                <div>
                  <div style={{ color: '#8C5A00', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ennustus täna</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#8C5A00' }}>
                    {profitForecast.todayTotal >= 0 ? '+' : ''}{profitForecast.todayTotal.toFixed(2)} €
                  </div>
                  <div style={{ color: '#8C5A00', fontSize: 10 }}>arbitraaž + toetus</div>
                </div>
                <div>
                  <div style={{ color: '#8C5A00', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ennustus homme</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#8C5A00' }}>
                    {profitForecast.tomorrowTotal !== 0
                      ? `${profitForecast.tomorrowTotal >= 0 ? '+' : ''}${profitForecast.tomorrowTotal.toFixed(2)} €`
                      : '—'}
                  </div>
                  <div style={{ color: '#8C5A00', fontSize: 10 }}>
                    {profitForecast.horizonHours >= 48 ? 'homse hinnad väljas' : 'homset veel pole'}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#8C5A00', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Toetus täna</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#15803d' }}>
                    +{profitForecast.todayRenewable.toFixed(2)} €
                  </div>
                  <div style={{ color: '#8C5A00', fontSize: 10 }}>5.37 c/kWh × netto-eksport</div>
                </div>
                <div>
                  <div style={{ color: '#8C5A00', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Arbitraaž täna</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: profitForecast.todayArbitrage >= 0 ? '#15803d' : '#dc2626' }}>
                    {profitForecast.todayArbitrage >= 0 ? '+' : ''}{profitForecast.todayArbitrage.toFixed(2)} €
                  </div>
                  <div style={{ color: '#8C5A00', fontSize: 10 }}>EMHASS osta-müü</div>
                </div>
              </div>
            )}

            {/* Timeline riba — joondub chart-i plot-alaga.
                Vasak Y-axis ~60px (c/kWh), parem kWh axis 42px + chart margin.right 20px = 62px paremalt. */}
            <div style={{ fontSize: 12, color: '#8C8C8C', marginBottom: 4, paddingLeft: 60 }}>Inverteri plaan</div>
            <div style={{ display: 'flex', height: 20, overflow: 'hidden', borderRadius: 2, marginLeft: 60, marginRight: 62 }}>
              {plan.map((mode, i) => (
                <div
                  key={i}
                  title={`${formatTime(i)} — ${MODES[mode]?.label || mode}`}
                  style={{
                    flex: 1,
                    background: MODES[mode]?.color || '#ccc',
                    borderRight: i < SLOTS_PER_DAY - 1 ? '0px solid transparent' : 'none',
                  }}
                />
              ))}
            </div>

            {/* Graafik */}
            <div style={{ marginTop: 16, height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f1f4" />
                  <XAxis
                    dataKey="time" stroke="#9E9E9E" fontSize={11}
                    ticks={chartData.filter((_, i) => i % 8 === 0).map(d => d.time)}
                  />
                  <YAxis yAxisId="price" stroke="#9E9E9E" fontSize={11}
                    label={{ value: 'c/kWh', position: 'insideLeft', angle: -90, fill: '#9E9E9E', fontSize: 11 }}
                    tickFormatter={(v) => v?.toFixed?.(1)}
                  />
                  {/* SoC peidetud Y-axis (0-100 %), et joon mahuks chartile ilma numbriskaalata */}
                  <YAxis yAxisId="soc" orientation="right" domain={[0, 100]} hide />
                  <YAxis yAxisId="kwh" orientation="right" domain={[0, 'auto']} stroke="#D4A017" fontSize={11}
                    width={42} tick={{ fill: '#D4A017' }}
                    tickFormatter={(v) => v?.toFixed?.(1)}
                    label={{ value: 'kWh / 15min', position: 'top', fill: '#D4A017', fontSize: 10, offset: 8 }}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0]?.payload;
                      return (
                        <div style={{ background: 'white', padding: 8, border: '1px solid #DDDFE1', borderRadius: 4, fontSize: 12 }}>
                          <div style={{ fontWeight: 600 }}>{label}</div>
                          {p?.market != null && <div style={{ color: '#0e7490' }}>Turuhind: {p.market.toFixed(2)} c/kWh</div>}
                          {p?.buy    != null && <div style={{ color: '#dc2626' }}>Ostuhind: {p.buy.toFixed(2)} c/kWh</div>}
                          {p?.sell   != null && <div style={{ color: '#15803d' }}>Müügihind: {p.sell.toFixed(2)} c/kWh</div>}
                          {p?.soc            != null && <div style={{ color: '#11093E' }}>Aku SoC: {p.soc.toFixed(0)} %</div>}
                          {p?.forecast_kwh   != null && <div style={{ color: '#B68A06' }}>Päikse ennustus: {p.forecast_kwh.toFixed(2)} kWh</div>}
                          {p?.actual_kwh     != null && <div style={{ color: '#F2B705' }}>Paneelide tootlus: {p.actual_kwh.toFixed(2)} kWh</div>}
                          {p?.grid_import    != null && <div style={{ color: '#1e3a8a' }}>Import: {p.grid_import.toFixed(2)} kWh</div>}
                          {p?.grid_export    != null && <div style={{ color: '#6b7280' }}>Eksport: {p.grid_export.toFixed(2)} kWh</div>}
                          {p?.aku_laadimine  != null && <div style={{ color: '#0284c7' }}>Aku laadimine: {p.aku_laadimine.toFixed(2)} kWh</div>}
                          {p?.aku_kasutus    != null && <div style={{ color: '#16a34a' }}>Aku kasutus: {p.aku_kasutus.toFixed(2)} kWh</div>}
                          {p?.tarbimine      != null && <div style={{ color: '#4b5563' }}>Kodune kasutus: {p.tarbimine.toFixed(2)} kWh</div>}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ width: 10, height: 10, borderRadius: '50%', background: MODES[p?.mode]?.color || '#ccc', display: 'inline-block' }} />
                            {MODES[p?.mode]?.label || p?.mode}
                          </div>
                        </div>
                      );
                    }}
                  />
                  {nowLabel && (
                    <ReferenceLine
                      yAxisId="price" x={nowLabel} stroke="#000" strokeWidth={1.5}
                      label={{ value: 'praegu', position: 'insideTopLeft', fill: '#000', fontSize: 10, fontWeight: 600 }}
                    />
                  )}
                  <Bar  yAxisId="kwh"   dataKey="forecast_kwh"  name="Päikese ennustus"   fill="#FBE6A8" fillOpacity={0.7}  barSize={5} hide={!!hidden.forecast_kwh} />
                  <Bar  yAxisId="kwh"   dataKey="actual_kwh"    name="Paneelide tootlus"  fill="#F2B705" fillOpacity={0.85} barSize={5} hide={!!hidden.actual_kwh} />
                  <Bar  yAxisId="kwh"   dataKey="grid_import"   name="Import"             fill="#1e3a8a" fillOpacity={0.6}  barSize={5} hide={!!hidden.grid_import} />
                  <Bar  yAxisId="kwh"   dataKey="grid_export"   name="Eksport"            fill="#9ca3af" fillOpacity={0.6}  barSize={5} hide={!!hidden.grid_export} />
                  <Bar  yAxisId="kwh"   dataKey="aku_laadimine" name="Aku laadimine"      fill="#7dd3fc" fillOpacity={0.7}  barSize={5} hide={!!hidden.aku_laadimine} />
                  <Bar  yAxisId="kwh"   dataKey="aku_kasutus"   name="Aku kasutus"        fill="#86efac" fillOpacity={0.7}  barSize={5} hide={!!hidden.aku_kasutus} />
                  <Bar  yAxisId="kwh"   dataKey="tarbimine"     name="Kodune kasutus"     fill="#4b5563" fillOpacity={0.5}  barSize={5} hide={!!hidden.tarbimine} />
                  <Line yAxisId="price" type="monotone" dataKey="market" name="Turuhind"  stroke="#0e7490" strokeWidth={2} dot={false} hide={!!hidden.market} />
                  <Line yAxisId="price" type="monotone" dataKey="buy"    name="Ostuhind"  stroke="#dc2626" strokeWidth={1.5} strokeDasharray="4 2" dot={false} hide={!!hidden.buy} />
                  <Line yAxisId="price" type="monotone" dataKey="sell"   name="Müügihind" stroke="#15803d" strokeWidth={1.5} strokeDasharray="4 2" dot={false} hide={!!hidden.sell} />
                  <Line yAxisId="soc"   type="monotone" dataKey="soc"    name="Aku SoC"   stroke="#11093E" strokeWidth={2} dot={false} connectNulls={false} hide={!!hidden.soc} />
                  <Line yAxisId="soc"   type="monotone" dataKey="soc_predicted" name="SoC ennustus (auto)" stroke="#11093E" strokeWidth={1.5} strokeDasharray="3 3" strokeOpacity={0.6} dot={false} connectNulls={true} hide={!!hidden.soc_predicted} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Chart sarjade legend — klikitav (peida/näita üksikuid sarju) */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12, fontSize: 13, color: '#3F413C' }}>
              {[
                { key: 'actual_kwh',     label: 'Paneelide tootlus', color: '#F2B705' },
                { key: 'forecast_kwh',   label: 'Päikese ennustus',  color: '#FBE6A8' },
                { key: 'aku_laadimine',  label: 'Aku laadimine',     color: '#7dd3fc' },
                { key: 'aku_kasutus',    label: 'Aku kasutus',       color: '#86efac' },
                { key: 'tarbimine',      label: 'Kodune kasutus',    color: '#4b5563' },
                { key: 'grid_import',    label: 'Import',            color: '#1e3a8a' },
                { key: 'grid_export',    label: 'Eksport',           color: '#9ca3af' },
                { key: 'soc',            label: 'Aku (%)',           color: '#11093E' },
                { key: 'market',         label: 'Turuhind',          color: '#0e7490' },
                { key: 'buy',            label: 'Ostuhind',          color: '#dc2626' },
                { key: 'sell',           label: 'Müügihind',         color: '#15803d' },
              ].map(({ key, label, color }) => {
                const off = !!hidden[key];
                return (
                  <button key={key} onClick={() => toggleHidden(key)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '4px 8px', borderRadius: 4,
                      border: '1px solid var(--border-strong, #DDDFE1)',
                      background: 'white', cursor: 'pointer',
                      fontSize: 13, color: off ? '#9ca3af' : '#3F413C',
                      textDecoration: off ? 'line-through' : 'none',
                    }}>
                    <span style={{
                      display: 'inline-block', width: 10, height: 10, borderRadius: 2,
                      background: off ? '#e5e7eb' : color,
                    }} />
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Mode-värvide legend */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 12, fontSize: 12, color: '#9ca3af' }}>
              <span style={{ fontWeight: 500 }}>Plaani moodid:</span>
              {Object.entries(MODES).map(([key, m]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: m.color }} />
                  {m.label}
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>

      {/* Muuda plaani modal */}
      {modalOpen && (
        <PlanModal
          plan={plan}
          onClose={() => setModalOpen(false)}
          onSave={async (newPlan, editMode) => {
            setPlan(newPlan);
            await savePlan(selectedSlot, dateStr, newPlan, editMode);
            setModalOpen(false);
          }}
          selectedPark={selectedPark}
          dateStr={dateStr}
          currentSlot={currentSlot}
          prices={prices}
        />
      )}
    </div>
  );
}

// ───────────── Komponendid ─────────────

function ToggleGroup({ children }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'row', gap: 2, padding: 2,
      background: '#F0F2F2', border: '1px solid #DDDFE1', borderRadius: 4,
    }}>
      {children}
    </div>
  );
}

function ToggleBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? '#FFFFFF' : '#F0F2F2',
        border: active ? '1px solid #FFFFFF' : '1px solid transparent',
        borderRadius: 2, color: '#000', fontSize: 14, fontWeight: 600,
        padding: '5px 16px', height: 33, cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function PlanModal({ plan, onClose, onSave, selectedPark, dateStr, currentSlot, prices = [] }) {
  const [draft, setDraft] = useState([...plan]);
  const [editMode, setEditMode] = useState('locked_all'); // 'locked_all' | 'locked_some'
  // Möödunud aegu ei näita — algselt esimene avatav slot on praegune (või 0, kui pole täna)
  const minSlot = currentSlot != null ? currentSlot : 0;
  const [bulkFrom, setBulkFrom] = useState(minSlot);
  const [bulkTo, setBulkTo] = useState(SLOTS_PER_DAY - 1);
  const [bulkMode, setBulkMode] = useState(DEFAULT_MODE);

  // Hinnaloend per-slot (96 × 15min). Iga 15min slot saab oma tunni-hinna.
  const priceBySlot = useMemo(() => {
    const dayStart = new Date(dateStr + 'T00:00:00').getTime();
    const arr = new Array(SLOTS_PER_DAY).fill(null);
    prices.forEach(p => {
      const slotIdx = Math.floor((p.ts - dayStart) / (15 * 60_000));
      if (slotIdx < 0 || slotIdx >= SLOTS_PER_DAY) return;
      // Iga hinnapunkt katab 4 × 15min (1 tund)
      for (let k = 0; k < 4; k++) {
        const idx = slotIdx + k;
        if (idx < SLOTS_PER_DAY && arr[idx] == null) {
          arr[idx] = {
            market: (p.price_kwh || 0) * 100,
            buy:    (p.price_buy_kwh || 0) * 100,
            sell:   (p.price_sell_kwh || 0) * 100,
          };
        }
      }
    });
    return arr;
  }, [prices, dateStr]);

  // Min/max hind, et värvida hinnad heatmap-stiilis
  const priceRange = useMemo(() => {
    const vals = priceBySlot.filter(p => p?.market != null).map(p => p.market);
    if (!vals.length) return { min: 0, max: 1 };
    return { min: Math.min(...vals), max: Math.max(...vals) };
  }, [priceBySlot]);

  const applyBulk = () => {
    const next = [...draft];
    const lo = Math.max(minSlot, Math.min(bulkFrom, bulkTo));
    const hi = Math.max(bulkFrom, bulkTo);
    for (let i = lo; i <= hi; i++) next[i] = bulkMode;
    setDraft(next);
  };

  return (
    <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#FFFFFF', border: '1px solid #DDDFE1', borderRadius: 8,
        boxShadow: '0 2px 8px -2px rgba(21,21,21,0.08), 0 6px 12px -2px rgba(21,21,21,0.08)',
        padding: 20, width: 880, maxWidth: '95%', maxHeight: '85vh', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: '#000', margin: 0 }}>
            Muuda plaani — {selectedPark?.name} · {dateStr}
          </h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', padding: '0 4px' }}>
            <X size={20} />
          </button>
        </div>
        <hr style={{ margin: '0 -20px 16px', border: 'none', borderTop: '1px solid #DDDFE1' }} />

        {/* Radio: režiim */}
        <label style={{ fontSize: 14, fontWeight: 500, color: '#535750' }}>Plaani muutmise režiim</label>
        <div style={{ display: 'flex', gap: 16, marginTop: 8, marginBottom: 12 }}>
          {[
            { id: 'locked_all', label: 'Lukusta kogu periood' },
            { id: 'locked_some', label: 'Üksikud ajavahemikud' },
          ].map(opt => (
            <label key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14, color: '#11093E' }}>
              <input type="radio" checked={editMode === opt.id} onChange={() => setEditMode(opt.id)} style={{ accentColor: '#1493C9' }} />
              {opt.label}
            </label>
          ))}
        </div>

        {/* Infokast */}
        <div style={{ background: '#FDF0E1', borderRadius: 6, padding: 16, marginBottom: 16, fontSize: 12, color: '#535750', fontWeight: 500 }}>
          {editMode === 'locked_all'
            ? 'Kogu plaan lukustatakse täpselt sellisena, nagu see on määratud. AI ei tee ühtegi muudatust, mistõttu plaan võib tingimuste muutudes osutuda ebaoptimaaleks.'
            : 'Ainult muudetud ajavahemikud jäävad fikseerituks ja AI neid ei muuda. Teisi ajavahemikke võidakse endiselt kohandada vastavalt tingimuste muutumisele.'}
        </div>

        {/* Bulk edit toolbar (locked_all režiimis) */}
        {editMode === 'locked_all' && (
          <div style={{ border: '1px solid #DDDFE1', borderRadius: 6, padding: 12, marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
            <span style={{ color: '#535750' }}>Alates</span>
            <SlotSelect value={bulkFrom} onChange={setBulkFrom} minSlot={minSlot} />
            <span style={{ color: '#535750' }}>kuni</span>
            <SlotSelect value={bulkTo} onChange={setBulkTo} minSlot={minSlot} />
            <ModeSelect value={bulkMode} onChange={setBulkMode} />
            <button onClick={applyBulk} style={{
              fontSize: 14, fontWeight: 600, color: '#000', background: '#F0F2F2',
              border: '1px solid #DDDFE1', borderRadius: 4, height: 33, padding: '5px 16px', cursor: 'pointer',
            }}>Rakenda</button>
          </div>
        )}

        {/* Tabel */}
        <div style={{ overflowY: 'auto', flex: 1, border: '1px solid #DDDFE1', borderRadius: 4 }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead style={{ background: '#F9FAF9', position: 'sticky', top: 0 }}>
              <tr>
                <th style={thStyle}>Aeg</th>
                <th style={thStyle}>Turuhind</th>
                <th style={thStyle}>Ostu / Müük</th>
                <th style={thStyle}>Režiim</th>
              </tr>
            </thead>
            <tbody>
              {draft.map((mode, i) => {
                if (i < minSlot) return null; // möödunud aegu ei näita
                const isCurrent = currentSlot === i;
                const pr = priceBySlot[i];
                // Heatmap värv: madal = roheline, kõrge = punane
                let priceBg = 'transparent';
                if (pr?.market != null && priceRange.max > priceRange.min) {
                  const norm = (pr.market - priceRange.min) / (priceRange.max - priceRange.min);
                  // 0 = roheline (#dcfce7), 1 = punane (#fecaca)
                  const r = Math.round(220 + (254 - 220) * norm);
                  const g = Math.round(252 - (252 - 202) * norm);
                  const b = Math.round(231 - (231 - 202) * norm);
                  priceBg = `rgb(${r},${g},${b})`;
                }
                return (
                  <tr key={i} style={{ height: 40, background: isCurrent ? '#FDF0E1' : '#FFFFFF' }}>
                    <td style={{ ...tdStyle, fontWeight: isCurrent ? 600 : 400 }}>
                      {formatTime(i)}–{formatTime(i + 1) === '00:00' ? '24:00' : formatTime(i + 1)}
                    </td>
                    <td style={{ ...tdStyle, background: priceBg, fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>
                      {pr?.market != null ? `${pr.market.toFixed(1)} c/kWh` : '—'}
                    </td>
                    <td style={{ ...tdStyle, fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'var(--text-3)' }}>
                      {pr?.buy != null ? (
                        <>
                          <span style={{ color: '#dc2626' }}>{pr.buy.toFixed(1)}</span>
                          {' / '}
                          <span style={{ color: '#15803d' }}>{pr.sell.toFixed(1)}</span>
                        </>
                      ) : '—'}
                    </td>
                    <td style={tdStyle}>
                      <ModeSelect
                        value={mode}
                        onChange={(m) => { const next = [...draft]; next[i] = m; setDraft(next); }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', flexDirection: 'row-reverse', gap: 12, paddingTop: 15 }}>
          <button onClick={() => onSave(draft, editMode)} style={{
            background: '#11093E', color: '#FFFFFF', border: 'none', borderRadius: 3.5,
            fontSize: 14, fontWeight: 600, padding: '5px 16px', height: 32, cursor: 'pointer',
          }}>
            <Lock size={12} style={{ marginRight: 6, verticalAlign: 'middle' }} />
            Salvesta
          </button>
          <button onClick={onClose} style={{
            background: 'transparent', color: '#3F413C', border: 'none', borderRadius: 3.5,
            fontSize: 14, fontWeight: 600, padding: '5px 16px', height: 32, cursor: 'pointer',
          }}>Tühista</button>
        </div>
      </div>
    </div>
  );
}

function ModeSelect({ value, onChange }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: '#F0F2F2', border: '1px solid #DDDFE1', borderRadius: 6, padding: '2px 6px',
    }}>
      <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', background: MODES[value]?.color || '#ccc' }} />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ border: 'none', background: 'transparent', fontSize: 14, color: '#3F413C', outline: 'none', minWidth: 200 }}
      >
        {MODE_KEYS.map(k => (
          <option key={k} value={k}>{MODES[k].label}</option>
        ))}
      </select>
      <ChevronDown size={14} style={{ color: '#666' }} />
    </div>
  );
}

function SlotSelect({ value, onChange, minSlot = 0 }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      style={{ background: '#F0F2F2', border: '1px solid #DDDFE1', borderRadius: 4, fontSize: 14, color: '#3F413C', padding: '2px 6px' }}
    >
      {Array.from({ length: SLOTS_PER_DAY }, (_, i) => i)
        .filter(i => i >= minSlot)
        .map(i => (
          <option key={i} value={i}>{formatTime(i)}</option>
        ))}
    </select>
  );
}

const thStyle = {
  textAlign: 'left', padding: '8px 12px', fontSize: 14, fontWeight: 600, color: '#000',
  borderBottom: '2px solid #DDDFE1',
};
const tdStyle = {
  padding: '0 12px', fontSize: 14, color: '#3F413C', borderBottom: '1px solid #DDDFE1', verticalAlign: 'middle',
};

export default ControlPage;
